import { useState } from "react";
import { MdOutlinePhone, MdPersonOutline } from "react-icons/md";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import Modal, { ModalContent } from "../../elements/Modal";
import { GoLocation } from "react-icons/go";
import { AiOutlineTruck } from "react-icons/ai";
import { RestaurantOrderDetails } from "../../../redux/restaurantOrders/restaurantOrdersSlice";
import OrderSupportChatModal from "./OrderSupportChatModal";

// Legacy OrderType for backwards compatibility with other components
type LegacyOrderType = {
  id: string;
  deliveryTime: string;
  customer: {
    name: string;
    phone: string;
    location: string;
  };
  restaurant: {
    name: string;
    phone: string;
    location: string;
  };
  order: {
    totalPrice: string;
    orderItems: Array<{
      name: string;
      quantity: string;
      price: string;
    }>;
  };
  status: string;
};

type OrderType = RestaurantOrderDetails | LegacyOrderType;

const OrderModal = ({
  toggleModal,
  order,
  isSupportOpen: isSupportOpenProp,
}: {
  toggleModal: () => void;
  order: OrderType | null;
  isSupportOpen?: boolean;
}) => {
  const [showChatModal, setShowChatModal] = useState(false);

  // Helper function to check if order is using new API structure
  const isNewApiOrder = (order: OrderType): order is RestaurantOrderDetails => {
    return order && "orderInfo" in order;
  };

  // Helper functions to get data from either structure
  const getOrderId = () => {
    if (!order) return "";
    return isNewApiOrder(order) ? order.orderInfo.orderNumber : order.id;
  };

  const getCustomerName = () => {
    if (!order) return "";
    return isNewApiOrder(order)
      ? order.customerDetails.name || "Anonymous"
      : order.customer.name;
  };

  const getRestaurantName = () => {
    if (!order) return "";
    return isNewApiOrder(order)
      ? order.restaurantDetails[0]?.name || "Restaurant"
      : order.restaurant.name;
  };

  const getRestaurantManager = () => {
    if (!order) return "";
    return isNewApiOrder(order)
      ? order.restaurantDetails[0]?.manager || "N/A"
      : "N/A";
  };

  const getTotalAmount = () => {
    if (!order) return "0";
    return isNewApiOrder(order)
      ? order.orderInfo.totalAmount.toLocaleString()
      : order.order.totalPrice;
  };

  const getOrderItems = () => {
    if (!order) return [];
    if (isNewApiOrder(order)) {
      return order.orderItems.map((item) => ({
        name: item.name,
        quantity: item.quantity.toString(),
        price: item.totalPrice.toLocaleString(),
        image: item.image,
        description: item.mealDetails?.description || "",
      }));
    }
    return order.order.orderItems.map((item) => ({
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      description: "",
    }));
  };

  const getStatus = () => {
    if (!order) return "";
    return isNewApiOrder(order) ? order.orderInfo.status : order.status;
  };

  const getEstimatedTime = () => {
    if (!order) return "";
    if (isNewApiOrder(order)) {
      const eta = new Date(order.orderInfo.estimatedTime);
      const now = new Date();
      const diff = eta.getTime() - now.getTime();
      const minutes = Math.max(0, Math.floor(diff / (1000 * 60)));
      return minutes > 0 ? `${minutes} mins` : "Now";
    }
    return order.deliveryTime;
  };

  const getProgressPercentage = () => {
    const status = getStatus().toLowerCase();
    switch (status) {
      case "pending":
        return 20;
      case "preparing":
        return 40;
      case "ready":
        return 60;
      case "in-progress":
        return 80;
      case "delivered":
        return 100;
      default:
        return 20;
    }
  };

  const isSupportOpen = () => {
    // Use the prop if provided (from the order list)
    if (isSupportOpenProp !== undefined) {
      console.log("isSupportOpen from prop:", isSupportOpenProp);
      return isSupportOpenProp === true;
    }

    // Fallback to checking orderInfo (legacy support)
    if (!order) return false;

    if (isNewApiOrder(order)) {
      console.log("Order Info:", order.orderInfo);
      console.log("isSupportOpen value:", order.orderInfo.isSupportOpen);
      return order.orderInfo.isSupportOpen === true;
    }

    return false;
  };

  const getOrderIdForChat = () => {
    if (!order) return "";
    return isNewApiOrder(order) ? order.orderInfo.id : order.id;
  };

  return (
    <>
      <Modal
        open={true}
        onClose={toggleModal}
        className="flex items-center justify-center"
      >
        <ModalContent className="mx-3 p-6 rounded-md shadow-lg flex flex-col  bg-white w-[80%] md:w-[45%] lg:w-[40%] xl:w-[50%] font-mont">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h1 className="font-semibold text-2xl text-secondary pb-1">
              Order {getOrderId()}
            </h1>
            <div>
              {isSupportOpen() && (
                <button
                  onClick={() => setShowChatModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors shadow-md"
                  title="Open support chat"
                >
                  <IoChatbubbleEllipsesOutline className="w-5 h-5" />
                  <span className="text-xs font-medium">Chat</span>
                </button>
              )}
            </div>
          </div>
          <p className="text-gray-500">Complete order details and tracking</p>
          <section className="h-[30rem] overflow-y-auto px-4">
            <section className="my-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Order Progress</span>
                <span>{getProgressPercentage()}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full my-3 h-2">
                <div
                  className="bg-secondary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              <p>Estimated Delivery Time: {getEstimatedTime()}</p>
            </section>
            <section className="flex justify-between my-3">
              <article className="text-gray-500">
                <h3 className="text-lg font-medium text-black">
                  Customer Information:
                </h3>
                <div className="flex gap-3 my-4 items-center">
                  <span>
                    <MdPersonOutline className="w-6 h-6" />
                  </span>
                  <p>{getCustomerName()}</p>
                </div>
                <div className="flex gap-3 my-4 items-center">
                  <span>
                    <MdOutlinePhone className="w-6 h-6" />
                  </span>
                  <p>Contact via admin</p>
                </div>
                <div className="flex gap-3 my-4 items-center">
                  <span>
                    <GoLocation className="w-6 h-6" />
                  </span>
                  <p>Delivery address</p>
                </div>
              </article>
              <article className="text-gray-500">
                <h3 className="text-lg font-medium text-black text-right">
                  Restaurant Information:
                </h3>
                <div className="flex gap-3 my-4 items-center justify-end">
                  <span>
                    <AiOutlineTruck className="w-6 h-6" />
                  </span>
                  <p>{getRestaurantName()}</p>
                </div>
                <div className="flex gap-3 my-4 items-center justify-end">
                  <span>
                    <MdOutlinePhone className="w-6 h-6" />
                  </span>
                  <p>{getRestaurantManager()}</p>
                </div>
                <div className="flex gap-3 my-4 items-center justify-end">
                  <span>
                    <GoLocation className="w-6 h-6" />
                  </span>
                  <p>Manager: {getRestaurantManager()}</p>
                </div>
              </article>
            </section>
            <section>
              <h3 className="text-lg">Order Items</h3>
              <article>
                {getOrderItems().map((item, index) => {
                  const hasDescription =
                    item.description && item.description.length > 0;
                  return (
                    <div
                      key={index}
                      className="flex justify-between bg-gray-50 rounded-md font-medium p-3 my-2"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        {hasDescription && (
                          <p className="text-xs text-gray-500 mt-1">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p>x{item.quantity}</p>
                        <p className="text-sm text-gray-600">₦{item.price}</p>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between items-center py-3 text-secondary border-t">
                  <span className="font-bold text-lg">Total:</span>
                  <span className="font-bold text-lg">₦{getTotalAmount()}</span>
                </div>
              </article>
            </section>
            <section>
              <article className="w-fit mx-auto my-4">
                <button
                  type="button"
                  className="text-secondary p-3 w-60 bg-[#008B501F] mr-5"
                >
                  Contact Customer
                </button>
                <button
                  type="button"
                  className="text-orange100 p-3 w-60 bg-[#E99F161F]"
                >
                  Contact Restaurant
                </button>
              </article>
              <article className="mt-4">
                <h3>Order Status</h3>
                <div className="flex w-full items-center gap-3">
                  <select
                    name="order-status"
                    id="order-status"
                    className="bg-gray-100 p-2 w-10/12 rounded-md"
                  >
                    <option value="in-progress">In-Progress</option>
                    <option value="accept">Accept</option>
                    <option value="ready">Ready</option>
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Deliver</option>
                  </select>
                  <button
                    type="button"
                    className="w-40 bg-secondary p-2 text-white rounded-md"
                  >
                    Update
                  </button>
                </div>
              </article>
            </section>
          </section>
        </ModalContent>
      </Modal>
      {showChatModal && isSupportOpen() && (
        <OrderSupportChatModal
          orderId={getOrderIdForChat()}
          orderNumber={getOrderId()}
          onClose={() => setShowChatModal(false)}
        />
      )}
    </>
  );
};

export default OrderModal;
